import React from "react";
import { useQuery, useApolloClient } from "@apollo/client";
import { useLocation, useSearchParams } from "react-router-dom";
import Typography from "@mui/material/Typography";
import format from "date-fns/format";
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSortModel,
  GridActionsColDef,
  getGridStringOperators,
  getGridDateOperators,
  GridFilterOperator,
  GridFilterInputSingleSelect,
} from "@mui/x-data-grid-pro";
import ManualApplyGridFilterPanel from "@app.automotus.io/components/ManualApplyGridFilterPanel";
import {
  DataTableContainer,
  ACTIONS_COLUMN_TEXT,
  FilteredColumnsPanel,
  STANDARD_GRID_PROPS,
  STANDARD_COLUMN_HEADER_TITLE_SX,
  DataGridActionsLinkButton,
} from "@app.automotus.io/components/DataTableContainer";
import {
  GET_PERMITS,
  GetPermitsData,
  GetPermitsVars,
  GetPermitsWhereFilter,
  PermitsOrderBy,
  transformPermitRaw,
} from "common/graphql";
import {
  parseSortModel,
  serializeSortModel,
  parseFilterModelFromSearchParams,
  filterModelToSearchParams,
  OrderBy,
} from "common/graphql/queryString";
import type { PermitSummary, PermitApplicationStatus } from "common/types/permits";
import PermitStatusBadge from "@curbsuite/components/permits/PermitStatusBadge";
import { PermitsTableToolbar } from "./PermitsTableToolbar";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";

const PENDING_APPLICATIONS_DEFAULT_VISIBILITY: GridColumnVisibilityModel = {
  displayId: true,
  payerAccountName: true,
  permitTypeName: true,
  vehicleCount: true,
  applicationSubmittedAt: true,
  applicationStatus: true,
  actions: true,
};

const PAGE_SIZE = 100;
const DEFAULT_SORT = "applicationSubmittedAt+desc";

// Base filter for pending applications - defined outside component to avoid dependency issues.
// application_submitted_by_user_id IS NOT NULL excludes vendor-ingested permits, which have no
// human submitter and should not appear in the ops review queue.
const PENDING_APPLICATIONS_FILTER: GetPermitsWhereFilter = {
  application_status: { _in: ["pending", "awaiting_additional_info"] as PermitApplicationStatus[] },
  application_submitted_by_user_id: { _is_null: false },
};

const formatDateValue = (dateString: string | null): string => {
  if (!dateString) {
    return "—";
  }
  return format(new Date(dateString), "MMM d, yyyy");
};

export interface PendingApplicationsDataGridProps {
  permitTypeOptions?: { value: string; label: string }[];
}

export const PendingApplicationsDataGrid: React.FC<PendingApplicationsDataGridProps> = ({ permitTypeOptions = [] }) => {
  const client = useApolloClient();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams({
    page: "1",
    sort: DEFAULT_SORT,
  });

  // Carried on each row's detail link so its own back button can restore this exact list view
  // even when the link is opened in a new tab, where react-router's `state` (passed separately
  // below) never arrives: a fresh tab has no access to the originating tab's in-memory history
  // state, only to whatever is in the URL itself.
  const returnTo = `${location.pathname}?${searchParams.toString()}`;

  // Page state
  const page = Number(searchParams.get("page") || 1);
  const handlePageChange = (newPage: number) => {
    const newPageOneIndexed = newPage + 1;
    if (newPageOneIndexed !== page) {
      searchParams.set("page", `${newPageOneIndexed}`);
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Sort state
  const sort = searchParams.get("sort") || DEFAULT_SORT;
  const sortModel: GridSortModel = React.useMemo(() => parseSortModel(sort), [sort]);
  const handleSortModelChange = (newSortModel: GridSortModel) => {
    searchParams.set("sort", serializeSortModel(newSortModel));
    searchParams.set("page", "1");
    setSearchParams(searchParams, { replace: true });
  };

  // Convert sort model to GraphQL order by
  const orderBy: PermitsOrderBy[] = React.useMemo(() => {
    if (sortModel.length === 0) {
      return [{ application_submitted_at: "desc" as OrderBy }];
    }
    const { field: sortField, sort: sortDirection } = sortModel[0];
    const direction: OrderBy = (sortDirection || "desc") as OrderBy;

    switch (sortField) {
      case "displayId":
        return [{ display_id: direction }];
      case "applicationSubmittedAt":
        return [{ application_submitted_at: direction }];
      case "applicationStatus":
        return [{ application_status: direction }];
      case "payerAccountName":
        return [{ payer_account: { holder_name: direction } }];
      case "permitTypeName":
        return [{ permit_type: { name: direction } }];
      default:
        return [{ application_submitted_at: direction }];
    }
  }, [sortModel]);

  // Filter state
  const appliedFilters = React.useMemo(() => parseFilterModelFromSearchParams(searchParams), [searchParams]);
  const [filterModel, setFilterModel] = React.useState<GridFilterModel | undefined>(appliedFilters);

  const handleApplyFilters = (newFilterModel: GridFilterModel) => {
    searchParams.set("page", "1");
    const filterParams = filterModelToSearchParams(newFilterModel, searchParams);
    setSearchParams(filterParams, { replace: true });
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    if (newFilterModel.items.length === 0 && filterModel?.items?.length) {
      handleApplyFilters(newFilterModel);
    }
    if (newFilterModel !== filterModel) {
      setFilterModel(newFilterModel);
    }
  };

  // Build GraphQL filter from filter model
  const filter = React.useMemo((): GetPermitsWhereFilter => {
    if (!appliedFilters || appliedFilters.items.length === 0) {
      return PENDING_APPLICATIONS_FILTER;
    }

    const columnFilters = appliedFilters.items
      .map((item) => {
        if (item.columnField === "displayId") {
          return {
            display_id: item.operatorValue === "equals" ? { _eq: item.value } : { _ilike: `%${item.value}%` },
          };
        }
        if (item.columnField === "payerAccountName") {
          return {
            payer_account: {
              _or: [
                { holder_name: item.operatorValue === "equals" ? { _eq: item.value } : { _ilike: `%${item.value}%` } },
                {
                  organization_name:
                    item.operatorValue === "equals" ? { _eq: item.value } : { _ilike: `%${item.value}%` },
                },
              ],
            },
          };
        }
        if (item.columnField === "permitTypeName" && item.operatorValue === "equals") {
          return {
            permit_type: { name: { _eq: item.value } },
          };
        }
        if (item.columnField === "applicationStatus" && item.operatorValue === "equals") {
          return {
            application_status: { _eq: item.value as PermitApplicationStatus },
          };
        }
        if (item.columnField === "applicationSubmittedAt") {
          const filterDate = new Date(`${item.value}T00:00:00`);
          const dateStart = startOfDay(filterDate);
          const dateEnd = endOfDay(filterDate);

          switch (item.operatorValue) {
            case "is":
              return { application_submitted_at: { _gte: dateStart.toISOString(), _lte: dateEnd.toISOString() } };
            case "before":
              return { application_submitted_at: { _lt: dateStart.toISOString() } };
            case "after":
              return { application_submitted_at: { _gt: dateEnd.toISOString() } };
          }
        }
        return undefined;
      })
      .filter(Boolean) as GetPermitsWhereFilter[];

    if (columnFilters.length === 0) {
      return PENDING_APPLICATIONS_FILTER;
    }

    if (appliedFilters.linkOperator === "and") {
      return {
        _and: [PENDING_APPLICATIONS_FILTER, ...columnFilters],
      };
    } else {
      return {
        _and: [PENDING_APPLICATIONS_FILTER, { _or: columnFilters }],
      };
    }
  }, [appliedFilters]);

  // Query permits
  const { data, loading, error } = useQuery<GetPermitsData, GetPermitsVars>(GET_PERMITS, {
    variables: {
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      orderBy,
      filter,
    },
    fetchPolicy: "network-only",
  });

  // Transform data for DataGrid
  const rows: PermitSummary[] = React.useMemo(() => {
    return data?.permits.map(transformPermitRaw) || [];
  }, [data?.permits]);

  const rowCount = data?.permitsAggregate.aggregate.count || 0;

  // Export functions
  const fetchTotalCount = async () => {
    const result = await client.query<GetPermitsData, GetPermitsVars>({
      query: GET_PERMITS,
      variables: {
        offset: 0,
        limit: 0,
        orderBy,
        filter,
      },
    });
    return result.data?.permitsAggregate.aggregate.count || 0;
  };

  const fetchDataBatch = async (offset: number, limit: number) => {
    const result = await client.query<GetPermitsData, GetPermitsVars>({
      query: GET_PERMITS,
      variables: {
        offset,
        limit,
        orderBy,
        filter,
      },
    });
    return result.data?.permits.map(transformPermitRaw) || [];
  };

  // Column definitions
  const columns: GridColDef<PermitSummary>[] = React.useMemo(() => {
    const displayIdColumn: GridColDef<PermitSummary> = {
      field: "displayId",
      headerName: "Application ID",
      flex: 1,
      sortable: true,
      filterOperators: getGridStringOperators().filter((op) =>
        ["equals", "contains"].includes(op.value),
      ) as GridFilterOperator<PermitSummary, string, string>[],
      renderCell: (params) => <Typography sx={{ fontFamily: "monospace", fontWeight: 500 }}>{params.value}</Typography>,
    };

    const payerAccountNameColumn: GridColDef<PermitSummary> = {
      field: "payerAccountName",
      headerName: "Operator",
      flex: 1,
      minWidth: 150,
      sortable: false,
      valueGetter: (params) => params.row.payerAccount?.organizationName || params.row.payerAccount?.holderName || "—",
      filterOperators: getGridStringOperators().filter((op) =>
        ["equals", "contains"].includes(op.value),
      ) as GridFilterOperator<PermitSummary, string, string>[],
    };

    const permitTypeNameColumn: GridColDef<PermitSummary> = {
      field: "permitTypeName",
      headerName: "Permit Type",
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.permitType?.name || "—",
      type: "singleSelect",
      valueOptions: permitTypeOptions,
      filterOperators: getGridStringOperators()
        .filter((op) => ["equals"].includes(op.value))
        .map((operator) => ({
          ...operator,
          InputComponent: operator.InputComponent ? GridFilterInputSingleSelect : undefined,
        })) as GridFilterOperator<PermitSummary, string, string>[],
    };

    const vehicleCountColumn: GridColDef<PermitSummary> = {
      field: "vehicleCount",
      headerName: "Vehicles",
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
    };

    const applicationSubmittedAtColumn: GridColDef<PermitSummary> = {
      field: "applicationSubmittedAt",
      headerName: "Submitted",
      flex: 1,
      type: "date",
      sortable: true,
      valueGetter: (params) => (params.row.applicationSubmittedAt ? new Date(params.row.applicationSubmittedAt) : null),
      valueFormatter: (params) => formatDateValue(params.value?.toISOString() || null),
      filterOperators: getGridDateOperators().filter((op) =>
        ["is", "before", "after"].includes(op.value),
      ) as GridFilterOperator<PermitSummary, Date, string>[],
    };

    const applicationStatusColumn: GridColDef<PermitSummary> = {
      field: "applicationStatus",
      headerName: "Status",
      flex: 1,
      sortable: false,
      type: "singleSelect",
      valueOptions: [
        { value: "pending", label: "Pending" },
        { value: "awaiting_additional_info", label: "Awaiting Info" },
      ],
      filterOperators: getGridStringOperators()
        .filter((op) => ["equals"].includes(op.value))
        .map((operator) => ({
          ...operator,
          InputComponent: operator.InputComponent ? GridFilterInputSingleSelect : undefined,
        })) as GridFilterOperator<PermitSummary, string, string>[],
      renderCell: (params) => <PermitStatusBadge type="application" status={params.value} />,
    };

    const actionsColumn: GridActionsColDef<PermitSummary> = {
      ...ACTIONS_COLUMN_TEXT,
      getActions: (params) => [
        <DataGridActionsLinkButton
          to={`/permits/${params.row.id}?returnTo=${encodeURIComponent(returnTo)}`}
          state={{ from: location.pathname }}
        >
          Review
        </DataGridActionsLinkButton>,
      ],
    };

    return [
      displayIdColumn,
      payerAccountNameColumn,
      permitTypeNameColumn,
      vehicleCountColumn,
      applicationSubmittedAtColumn,
      applicationStatusColumn,
      actionsColumn,
    ];
  }, [location.pathname, returnTo, permitTypeOptions]);

  return (
    <DataTableContainer
      tableName="pending-applications-table"
      defaultVisibilityModel={PENDING_APPLICATIONS_DEFAULT_VISIBILITY}
      sx={{ height: "100%" }}
    >
      {({ apiRef, initialState, isIconOnly, resetLayout }) => (
        <DataGridPro
          apiRef={apiRef}
          localeText={isIconOnly ? { toolbarFilters: "" } : undefined}
          components={{
            Toolbar: PermitsTableToolbar,
            FilterPanel: ManualApplyGridFilterPanel,
            ColumnsPanel: FilteredColumnsPanel,
          }}
          componentsProps={{
            filterPanel: {
              onApplyFilters: handleApplyFilters,
            },
            toolbar: {
              fetchTotalCount,
              fetchDataBatch,
              exportFileName: `permits_pending_${Date.now()}`,
              columns,
              defaultVisibilityModel: PENDING_APPLICATIONS_DEFAULT_VISIBILITY,
              isIconOnly,
              resetLayout,
            },
          }}
          columns={columns}
          rows={rows}
          rowCount={rowCount}
          loading={loading}
          {...STANDARD_GRID_PROPS}
          error={error}
          pagination
          page={page - 1}
          onPageChange={handlePageChange}
          pageSize={PAGE_SIZE}
          paginationMode="server"
          rowsPerPageOptions={[PAGE_SIZE]}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          initialState={initialState}
          sx={{
            flexGrow: 1,
            ...STANDARD_COLUMN_HEADER_TITLE_SX,
          }}
        />
      )}
    </DataTableContainer>
  );
};

export default PendingApplicationsDataGrid;
